import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLogo } from '../../context/LogoContext';
import { mediaPortalApi } from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Shield, Radio, ArrowRight, Users, Image,
  Link2, Settings, LogOut, Zap, ExternalLink, Monitor, Activity, Phone,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ems_token')}`,
});

const HubDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [hubSettings, setHubSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navDialog, setNavDialog] = useState(null); // { path, label, color }

  const hasEmsAccess = user?.roles?.some(r => ['super_admin', 'simcel', 'observer', 'participant'].includes(r));
  const hasMpAccess = user?.roles?.some(r => ['hub_admin', 'super_admin', 'simcel', 'observer'].includes(r)) || (user?.mp_roles?.length > 0);
  const isObserverOnly = user?.roles?.includes('observer') && !user?.roles?.some(r => ['super_admin', 'hub_admin', 'simcel'].includes(r));
  // V15.0 — Logistics access mirrors MP for now (hub_admin / super_admin can
  // always enter; simcel / observer / participant get gated by per-exercise
  // assignments once assigned). Kept broad so testers can see it immediately.
  const hasLogisticsAccess = user?.roles?.some(r => ['super_admin', 'hub_admin', 'simcel', 'observer', 'participant'].includes(r));

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, settingsRes] = await Promise.allSettled([
          mediaPortalApi.getHubStats(),
          fetch(`${API_URL}/hub/settings`, { headers: getHeaders() }).then(r => r.json()),
        ]);
        if (statsData.status === 'fulfilled') setStats(statsData.value);
        if (settingsRes.status === 'fulfilled') setHubSettings(settingsRes.value);
      } catch { /* noop — dashboard stats are best-effort */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAppClick = (path, label, color) => {
    setNavDialog({ path, label, color });
  };

  const pendingNavRef = React.useRef(null);

  const openInCurrentTab = () => {
    const path = navDialog?.path;
    if (path) {
      pendingNavRef.current = path;
      setNavDialog(null);
    }
  };

  const openInNewTab = () => {
    const path = navDialog?.path;
    if (path) {
      setNavDialog(null);
      window.open(window.location.origin + path, '_blank');
    }
  };

  React.useEffect(() => {
    if (!navDialog && pendingNavRef.current) {
      const path = pendingNavRef.current;
      pendingNavRef.current = null;
      navigate(path);
    }
  }, [navDialog, navigate]);

  const hubName = hubSettings?.hub_name || 'SpillConsult Hub';
  const emsName = hubSettings?.ems_name || 'Exercise Management';
  const mpName = hubSettings?.mp_name || 'Media Portal';

  // Theme-aware colors
  const bg = isDark ? 'bg-[#06070a]' : 'bg-muted';
  const text = isDark ? 'text-white' : 'text-foreground';
  const textMuted = isDark ? 'text-white/40' : 'text-muted-foreground';
  const textSubtle = isDark ? 'text-white/30' : 'text-muted-foreground';
  const textFaint = isDark ? 'text-white/20' : 'text-muted-foreground';
  const border = isDark ? 'border-white/5' : 'border-border';
  const borderCard = isDark ? 'border-white/[0.06]' : 'border-border';
  const cardBg = isDark ? '' : 'bg-white';
  const hoverBg = isDark ? 'hover:bg-white/5' : 'hover:bg-muted';
  const pillBg = isDark ? 'bg-white/5 border-white/10' : 'bg-muted border-border';
  const btnBg = isDark ? 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/[0.07] text-white/60 hover:text-white' : 'bg-white border-border hover:border-border hover:bg-muted text-muted-foreground hover:text-foreground';

  const { logoUrl: globalLogo } = useLogo();

  const LogoOrIcon = ({ src, fallback: Icon, className, iconColor, useGlobal = false }) => {
    const effectiveSrc = useGlobal ? (globalLogo || (src ? `${BACKEND}${src}` : null)) : (src ? `${BACKEND}${src}` : null);
    if (effectiveSrc) {
      return <img src={effectiveSrc} alt="" className={`w-full h-full object-contain p-1.5 ${className || ''}`} />;
    }
    return <Icon className={`w-7 h-7 ${iconColor}`} />;
  };

  return (
    <div className={`min-h-screen ${bg} ${text} relative overflow-hidden`} data-testid="hub-dashboard">
      {/* Grid background */}
      <div className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.06]'}`}
        style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'} 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] ${isDark ? 'bg-blue-500/5' : 'bg-blue-500/[0.03]'} rounded-full blur-[120px]`} />

      {/* Top Bar */}
      <div className={`relative z-10 flex items-center justify-between px-8 py-5 border-b ${border}`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center overflow-hidden">
            <LogoOrIcon src={hubSettings?.hub_logo} fallback={Zap} iconColor="text-white" useGlobal />
          </div>
          <span className="text-lg font-semibold tracking-tight">{hubName}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className={`text-xs ${textMuted}`}>{user?.email}</p>
          </div>
          <button onClick={handleLogout}
            className={`p-2 rounded-lg ${hoverBg} transition-colors ${textMuted}`}
            data-testid="hub-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-8 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Mission Control</h1>
          <p className={`${textMuted} text-sm`}>Select an application to manage. Both systems are linked and synchronized.</p>
        </div>

        {/* Integration Status */}
        {stats?.integration && (
          <div className="flex items-center gap-3 mb-8 text-xs" data-testid="hub-integration-status">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${pillBg} border`}>
              <Link2 className="w-3.5 h-3.5 text-emerald-400" />
              <span className={textMuted}>{stats.integration.linked_exercises} linked exercises</span>
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${pillBg} border`}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className={textMuted}>Systems Linked</span>
            </div>
          </div>
        )}

        {/* App Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* EMS Card */}
          {hasEmsAccess && (
            <button
              onClick={() => handleAppClick(isObserverOnly ? '/exercises' : '/dashboard', emsName, 'blue')}
              className={`group relative text-left p-0 rounded-2xl overflow-hidden border ${borderCard} ${cardBg} hover:border-blue-500/30 transition-all duration-300`}
              data-testid="hub-ems-card"
            >
              <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-blue-500/[0.04] to-cyan-500/[0.02] group-hover:from-blue-500/[0.08] group-hover:to-cyan-500/[0.04]' : 'bg-gradient-to-br from-blue-50 to-cyan-50/50 group-hover:from-blue-100/80 group-hover:to-cyan-50'} transition-all duration-300`} />
              <div className={`absolute top-0 right-0 w-40 h-40 ${isDark ? 'bg-blue-500/5 group-hover:bg-blue-500/10' : 'bg-blue-100/50 group-hover:bg-blue-200/60'} rounded-full blur-[60px] transition-all`} />

              <div className="relative p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-14 h-14 rounded-xl ${isDark ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/20' : 'bg-blue-100 border-blue-200'} border flex items-center justify-center overflow-hidden`}>
                    <LogoOrIcon src={hubSettings?.ems_logo} fallback={Shield} iconColor="text-blue-400" useGlobal />
                  </div>
                  <ArrowRight className={`w-5 h-5 ${textFaint} group-hover:text-blue-400 group-hover:translate-x-1 transition-all`} />
                </div>
                <h2 className="text-xl font-semibold mb-1 group-hover:text-blue-400 transition-colors">{emsName}</h2>
                <p className={`text-sm ${textSubtle} mb-6`}>Manage exercises, injects, responses, and real-time communications</p>
                {stats?.ems && (
                  <div className={`grid grid-cols-3 gap-4 pt-4 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <div>
                      <p className="text-xl font-bold text-blue-400">{stats.ems.exercises}</p>
                      <p className={`text-[11px] ${textSubtle}`}>Exercises</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-emerald-400">{stats.ems.active_exercises}</p>
                      <p className={`text-[11px] ${textSubtle}`}>Active</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-amber-400">{stats.ems.users}</p>
                      <p className={`text-[11px] ${textSubtle}`}>Users</p>
                    </div>
                  </div>
                )}
              </div>
            </button>
          )}

          {/* Media Portal Card */}
          {hasMpAccess && (
            <button
              onClick={() => handleAppClick(isObserverOnly ? '/mp/exercises' : '/mp/dashboard', mpName, 'purple')}
              className={`group relative text-left p-0 rounded-2xl overflow-hidden border ${borderCard} ${cardBg} hover:border-purple-500/30 transition-all duration-300`}
              data-testid="hub-mp-card"
            >
              <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-purple-500/[0.04] to-pink-500/[0.02] group-hover:from-purple-500/[0.08] group-hover:to-pink-500/[0.04]' : 'bg-gradient-to-br from-purple-50 to-pink-50/50 group-hover:from-purple-100/80 group-hover:to-pink-50'} transition-all duration-300`} />
              <div className={`absolute top-0 right-0 w-40 h-40 ${isDark ? 'bg-purple-500/5 group-hover:bg-purple-500/10' : 'bg-purple-100/50 group-hover:bg-purple-200/60'} rounded-full blur-[60px] transition-all`} />

              <div className="relative p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-14 h-14 rounded-xl ${isDark ? 'bg-gradient-to-br from-purple-500/20 to-pink-600/10 border-purple-500/20' : 'bg-purple-100 border-purple-200'} border flex items-center justify-center overflow-hidden`}>
                    <LogoOrIcon src={hubSettings?.mp_logo} fallback={Radio} iconColor="text-purple-400" useGlobal />
                  </div>
                  <ArrowRight className={`w-5 h-5 ${textFaint} group-hover:text-purple-400 group-hover:translate-x-1 transition-all`} />
                </div>
                <h2 className="text-xl font-semibold mb-1 group-hover:text-purple-400 transition-colors">{mpName}</h2>
                <p className={`text-sm ${textSubtle} mb-6`}>Simulate media posts, schedule content across platforms, and monitor feeds</p>
                {stats?.media_portal && (
                  <div className={`grid grid-cols-3 gap-4 pt-4 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <div>
                      <p className="text-xl font-bold text-purple-400">{stats.media_portal.posts}</p>
                      <p className={`text-[11px] ${textSubtle}`}>Posts</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-emerald-400">{stats.media_portal.published}</p>
                      <p className={`text-[11px] ${textSubtle}`}>Published</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-amber-400">{stats.media_portal.platforms}</p>
                      <p className={`text-[11px] ${textSubtle}`}>Platforms</p>
                    </div>
                  </div>
                )}
              </div>
            </button>
          )}

          {/* V15.0 — Logistics & Spend Command tile. Copper-accent to match the
              app's own "Tactical Finance" palette.
              Non-admin roles see a "Coming Soon" version that is not clickable. */}
          {hasLogisticsAccess && (() => {
            const isLogisticsAdmin = user?.roles?.some(r => ['super_admin', 'hub_admin'].includes(r));
            if (isLogisticsAdmin) {
              return (
                <button
                  onClick={() => handleAppClick('/hub/logistics', 'Logistics & Spend', 'orange')}
                  className={`group relative text-left p-0 rounded-2xl overflow-hidden border ${borderCard} ${cardBg} hover:border-orange-500/30 transition-all duration-300`}
                  data-testid="hub-logistics-card"
                >
                  <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-orange-500/[0.05] to-amber-500/[0.02] group-hover:from-orange-500/[0.1] group-hover:to-amber-500/[0.04]' : 'bg-gradient-to-br from-orange-50 to-amber-50/50 group-hover:from-orange-100/80 group-hover:to-amber-50'} transition-all duration-300`} />
                  <div className={`absolute top-0 right-0 w-40 h-40 ${isDark ? 'bg-orange-500/5 group-hover:bg-orange-500/10' : 'bg-orange-100/50 group-hover:bg-orange-200/60'} rounded-full blur-[60px] transition-all`} />
                  <div className="relative p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-14 h-14 rounded-xl ${isDark ? 'bg-gradient-to-br from-orange-500/20 to-amber-600/10 border-orange-500/20' : 'bg-orange-100 border-orange-200'} border flex items-center justify-center`}>
                        <Zap className="w-6 h-6 text-orange-400" />
                      </div>
                      <ArrowRight className={`w-5 h-5 ${textFaint} group-hover:text-orange-400 group-hover:translate-x-1 transition-all`} />
                    </div>
                    <h2 className="text-xl font-semibold mb-1 group-hover:text-orange-400 transition-colors">Logistics & Spend</h2>
                    <p className={`text-sm ${textSubtle} mb-6`}>Book hotels, transport, flights, food and procurement — one simulated wallet, one ledger</p>
                    <div className={`pt-4 border-t ${isDark ? 'border-white/5' : 'border-gray-100'} flex items-center gap-3`}>
                      <span className="text-[10px] font-semibold px-2 py-1 rounded bg-orange-500/10 text-orange-400 uppercase tracking-wider">New · V15</span>
                      <span className={`text-[11px] ${textSubtle}`}>Tactical Finance</span>
                    </div>
                  </div>
                </button>
              );
            }
            return (
              <div
                className={`group relative text-left p-0 rounded-2xl overflow-hidden border ${borderCard} ${cardBg} opacity-60 cursor-not-allowed`}
                data-testid="hub-logistics-card-coming-soon"
              >
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-orange-500/[0.03] to-amber-500/[0.01]' : 'bg-gradient-to-br from-orange-50/50 to-amber-50/30'}`} />
                <div className="relative p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-14 h-14 rounded-xl ${isDark ? 'bg-gradient-to-br from-orange-500/10 to-amber-600/5 border-orange-500/10' : 'bg-orange-100/60 border-orange-200/60'} border flex items-center justify-center`}>
                      <Zap className="w-6 h-6 text-orange-400/50" />
                    </div>
                  </div>
                  <h2 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white/50' : 'text-foreground/50'}`}>Logistics & Spend</h2>
                  <p className={`text-sm ${textSubtle} mb-6`}>Book hotels, transport, flights, food and procurement — one simulated wallet, one ledger</p>
                  <div className={`pt-4 border-t ${isDark ? 'border-white/5' : 'border-gray-100'} flex items-center gap-3`}>
                    <span className="text-[10px] font-semibold px-2 py-1 rounded bg-orange-500/10 text-orange-400/70 uppercase tracking-wider">Coming Soon</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Quick Actions */}
        {user?.roles?.includes('hub_admin') && (
          <div className="mt-8 flex gap-3 flex-wrap" data-testid="hub-admin-actions">
            <button onClick={() => navigate('/hub/users')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm ${btnBg}`}
              data-testid="hub-manage-users-btn">
              <Users className="w-4 h-4" /> Manage Users
            </button>
            <button onClick={() => navigate('/hub/settings')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm ${btnBg}`}
              data-testid="hub-settings-btn">
              <Image className="w-4 h-4" /> Branding
            </button>
            <button onClick={() => navigate('/hub/system')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm ${btnBg}`}
              data-testid="hub-system-btn">
              <Settings className="w-4 h-4" /> System
            </button>
            <button onClick={() => navigate('/hub/connections')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm ${btnBg}`}
              data-testid="hub-connections-btn">
              <Activity className="w-4 h-4" /> Connections &amp; Numbers
            </button>
          </div>
        )}
      </div>

      <div className={`absolute bottom-0 left-0 right-0 py-4 text-center text-[11px] ${isDark ? 'text-white/15' : 'text-muted-foreground'} z-10`}>
        {hubName} &middot; Exercise & Media Management Platform
      </div>

      {/* Navigation Dialog */}
      <Dialog open={!!navDialog} onOpenChange={(open) => !open && setNavDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              Open {navDialog?.label}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground mb-4">
            How would you like to open <strong>{navDialog?.label}</strong>?
          </DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={openInCurrentTab}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all hover:border-${navDialog?.color || 'blue'}-500/50 hover:bg-accent`}
              data-testid="nav-open-here"
            >
              <Monitor className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">Open here</span>
              <span className="text-[10px] text-muted-foreground">Replace current view</span>
            </button>
            <button
              onClick={openInNewTab}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all hover:border-${navDialog?.color || 'blue'}-500/50 hover:bg-accent`}
              data-testid="nav-open-new-tab"
            >
              <ExternalLink className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">New tab</span>
              <span className="text-[10px] text-muted-foreground">Keep Hub open</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HubDashboard;
